import { faBug } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Tooltip } from '@feathr/components';
import { cssVar, SSETransport, useRealtimeContext } from '@feathr/hooks';

import * as styles from './RealtimeDebuggerTrigger.css';

const RealtimeDebugger = lazy(() => import('./RealtimeDebugger'));

type TConnectionStatus = 'connected' | 'connecting' | 'disconnected';

function getIconColor(status: TConnectionStatus): string {
  switch (status) {
    case 'connected':
      return cssVar('--color-green-500');

    case 'connecting':
      return cssVar('--color-orange-500');

    case 'disconnected':
      return cssVar('--color-red-500');
  }
}

function RealtimeDebuggerTrigger(): JSX.Element {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { transport } = useRealtimeContext();
  const [connectionStatus, setConnectionStatus] = useState<TConnectionStatus>('disconnected');

  const isSSETransport = transport instanceof SSETransport;

  useEffect(() => {
    const checkConnection = (): void => {
      if (transport.isConnected) {
        setConnectionStatus('connected');
      } else if (isSSETransport && transport.currentReconnectAttempts > 0) {
        setConnectionStatus('connecting');
      } else {
        setConnectionStatus('disconnected');
      }
    };

    checkConnection();
    const intervalId = setInterval(checkConnection, 1000);
    return (): void => clearInterval(intervalId);
  }, [transport, isSSETransport]);

  const handleOpen = useCallback((): void => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback((): void => {
    setIsOpen(false);
  }, []);

  const iconColor = getIconColor(connectionStatus);

  return (
    <>
      <Tooltip title={t('Realtime debugger')}>
        <Button className={styles.trigger} onClick={handleOpen} type={'icon'}>
          <FontAwesomeIcon color={iconColor} icon={faBug} />
        </Button>
      </Tooltip>
      <Suspense fallback={null}>
        <RealtimeDebugger isOpen={isOpen} onClose={handleClose} />
      </Suspense>
    </>
  );
}

export default RealtimeDebuggerTrigger;
